import {useEffect} from 'react';
import _ from 'lodash';
import {CURRENCY_SYMBOLS} from '../../constants';

export const buildIndex = (items, parentIndex) => {
  return _.map(items, (_item, _idx) => {
    _item.rawIndex = [...parentIndex, _idx];
    _item.index = _.join(_item.rawIndex, '-');
    if (_.has(_item, 'content')) {
      _item.content = buildIndex(_item.content, [...parentIndex, _idx]);
    }
    return _item;
  });
};

export const parseIds = (document, idMap) => {
  if (_.has(document, 'attrs')) {
    const id = _.get(document, 'attrs.id');
    const {rawIndex, index, type} = _.pick(document, [
      'rawIndex',
      'index',
      'type',
    ]);
    idMap[id] = {id, rawIndex, index, type};
  }
  if (_.has(document, 'content')) {
    _.forEach(document.content, (_content) => parseIds(_content, idMap));
  }
  return idMap;
};

export const getItemId = (_item) => {
  if (_item.type === 'footings') {
    return _item.table_id;
  } else if (_item.type === 'inconsistencies') {
    if (_item.items[0].source === 'table') {
      return _item.items[0].table_id;
    } else {
      return _item.items[0].id;
    }
  } else {
    console.error('Unknown item type', _item);
  }
};

export const containsDateString = (value) => {
  // Check if the string matches common date formats
  const dateFormats = [
    /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD or YYYY-DD-MM
    /^\d{1,2}\/\d{1,2}\/\d{4}$/, // MM/DD/YYYY or DD/MM/YYYY
    /^\d{1,2}-\d{1,2}-\d{4}$/, // MM-DD-YYYY or DD-MM-YYYY
    /^[A-Za-z]+ \d{1,2}, \d{4}$/, // Month DD, YYYY
    /^[A-Za-z]{3} '\d{2}$/, // MMM 'YY
    /^[A-Za-z]+ '\d{2}$/, // Month 'YY
  ];

  // Test the string against each format
  for (const format of dateFormats) {
    if (format.test(value)) {
      return true;
    }
  }

  return false;
};

export const isValidYear = (value) => {
  const _yearRegex = /^(19|20)\d{2}$/;
  return _yearRegex.test(value);
};

export const getYearFromString = (value) => {
  if (isValidYear(value)) {
    return value;
  }
  if (/^.* '(\d{2})$/.test(value)) {
    return `20${value.match(/^.* '(\d{2})$/)[1]}`;
  }
  const _date = new Date(value);
  return _date.getFullYear().toString();
};

export const getLabel = (item) => {
  if (item.type === 'inconsistencies') {
    return item.title;
  }

  // Handle empty line_item and column_header for footings
  const lineItem = _.trim(item.line_item || '');
  const columnHeader = _.trim(item.column_header || '');
  if (!lineItem && !columnHeader) {
    return 'Unlabeled Cell';
  }

  let _year = '';
  let _label = '';
  if (containsDateString(columnHeader)) {
    _year = getYearFromString(columnHeader);
    _label = lineItem;
  } else if (containsDateString(lineItem)) {
    _year = getYearFromString(lineItem);
    _label = columnHeader;
  } else if (columnHeader) {
    return `${columnHeader} ${lineItem}`.trim();
  } else {
    return lineItem;
  }

  if (!_.isNil(_year)) {
    return `${_year} ${_label}`;
  }
  return _label;
};

export const formatCardValue = (value, valueUnit) => {
  if (_.trim(value) === '0' || value === 0 || _.trim(value) === '-') {
    return `${value}`;
  }
  switch (valueUnit) {
    case 'thousands':
      return `${value}K`;
    case 'millions':
      return `${value}M`;
    case 'billions':
      return `${value}B`;
    default:
      return `${value}`;
  }
};

export const getSortedDocumentIds = (editor) => {
  // Check if this is a multi-editor proxy with our special method
  if (editor && typeof editor.getAllEditorsJSON === 'function') {
    const editorsData = editor.getAllEditorsJSON();
    let allIds = [];

    // Collect IDs from all editors
    editorsData.forEach(({editorIndex, json}) => {
      try {
        const _traversedDocument = {
          content: buildIndex(json.content, [editorIndex]),
        };
        const _idMap = parseIds(_traversedDocument, {});

        // Add editor index to each ID for proper sorting across sections
        const _editorIds = _.values(_idMap).map((item) => ({
          ...item,
          editorIndex,
          // Update rawIndex to include editor ordering
          rawIndex: [editorIndex, ...item.rawIndex],
        }));

        allIds = allIds.concat(_editorIds);
      } catch (error) {
        console.warn('Error processing editor JSON for document IDs:', error);
      }
    });

    // Sort all IDs across all editors
    const _orderedIds = _.sortBy(allIds, [
      function (_item) {
        return _item.rawIndex[0]; // Editor index
      },
      function (_item) {
        return _item.rawIndex[1]; // Section index within editor
      },
      function (_item) {
        return _item.rawIndex[2]; // Element index within section
      },
    ]);

    return _.map(_orderedIds, 'id');
  }

  // Fallback to original single-editor behavior for V1 compatibility
  const _documentJSON = editor.getJSON();
  const _traversedDocument = {content: buildIndex(_documentJSON.content, [0])};
  const _idMap = parseIds(_traversedDocument, {});
  const _orderedIds = _.sortBy(_.values(_idMap), [
    function (_item) {
      return _item.rawIndex[0];
    },
    function (_item) {
      return _item.rawIndex[1];
    },
  ]);
  return _.map(_orderedIds, 'id');
};

export const parseFootingExpressions = (
  cellExpression,
  literalExpression,
  tableId
) => {
  const table = document.querySelector(
    `[data-id="${tableId}"][data-type="table"]`
  );
  if (!table) {
    throw new Error(`Table with id ${tableId} not found.`);
  }

  // Parse the cell-based expression to extract (row, col) pairs
  const cellParts = cellExpression.match(/([+-]?\s*\(\d+,\d+\))/g);
  const tableParts = cellParts.map((part) => {
    // Extract the operator and (row, col)
    const operatorMatch = part.match(/^([+-]?)\s*/);
    const operator = operatorMatch ? operatorMatch[1] : '+';
    const coordinatesMatch = part.match(/\((\d+),(\d+)\)/);
    if (!coordinatesMatch) {
      throw new Error(`Invalid coordinate format in part: ${part}`);
    }

    const row = parseInt(coordinatesMatch[1], 10);
    const col = parseInt(coordinatesMatch[2], 10);

    // Get the corresponding td element
    const td = table.querySelector(
      `tr:nth-child(${row + 1}) td:nth-child(${col + 1})`
    );
    if (!td) {
      throw new Error(`Cell not found at row ${row}, column ${col}.`);
    }

    let rawValue = td.textContent.trim();

    // Remove trailing ) ONLY if it's an unmatched alignment bracket
    // Count brackets to detect: "35,200.00)" has unmatched ), "$ (35,200.00)" is balanced
    const openCount = (rawValue.match(/\(/g) || []).length;
    const closeCount = (rawValue.match(/\)/g) || []).length;
    if (closeCount > openCount && rawValue.endsWith(')')) {
      rawValue = rawValue.slice(0, -1);
    }
    // Strip all currency symbols (handle S$ first since it's two characters)
    const currencySymbols = Object.values(CURRENCY_SYMBOLS).sort(
      (a, b) => b.length - a.length
    );
    let value = rawValue;
    for (const symbol of currencySymbols) {
      value = value.replace(symbol, '');
    }
    value = value.replace(/[%,\s]/g, '');

    // Convert '-' alone (accounting representation of zero) to '0' first
    if (value === '-') {
      value = '0';
    }

    // Normalize negative numbers: convert -X to (X) to match backend accounting format
    // Only convert if there are digits after the minus sign
    if (value.startsWith('-') && /\d/.test(value)) {
      value = '(' + value.slice(1) + ')';
    }

    // Add thousand separators
    value = value.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

    // Convert empty values or any numeric zero back to '0'
    const numericValue = parseFloat(value.replace(/[(),]/g, ''));
    if (value === '' || numericValue === 0) value = '0';

    // Return the value prefixed by the operator
    return operator + (operator ? ' ' : '') + value;
  });

  // Parse the literal value expression
  const literalParts = literalExpression.match(
    /([+-]?\s*\(?\d+(?:\.\d+)?\)?)/g
  );

  const calculatedParts = literalParts
    ? literalParts.map((part) => {
        const value = part.trim().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return value;
      })
    : [];
  return {tableParts, calculatedParts};
};

export const parsePdfFootingExpressions = (literalExpression) => {
  const literalParts = literalExpression.match(
    /([+-]?\s*\(?\d+(?:\.\d+)?\)?)/g
  );

  const calculatedParts = literalParts
    ? literalParts.map((part) => {
        const value = part.trim().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return value;
      })
    : [];

  return {tableParts: calculatedParts, calculatedParts};
};

export const CELL_CLICK_EVENT_NAME = 'footedCellClick';

export const footedCellClickEvent = (cellId) => {
  const event = new CustomEvent(CELL_CLICK_EVENT_NAME, {
    detail: {id: cellId},
  });

  document.documentElement.dispatchEvent(event);
};

export const useCellClickEventListener = (callback) => {
  useEffect(() => {
    function handler(event) {
      callback(event.detail.id);
    }

    document.documentElement.addEventListener(CELL_CLICK_EVENT_NAME, handler);

    return () => {
      document.documentElement.removeEventListener(
        CELL_CLICK_EVENT_NAME,
        handler
      );
    };
  }, [callback]);
};

// Utility functions to handle multiple editors
const executeCommandOnEditors = (editorOrEditors, commandPath, ...args) => {
  const editors = Array.isArray(editorOrEditors)
    ? editorOrEditors
    : [editorOrEditors];

  editors.forEach((editor) => {
    if (editor && editor.commands) {
      const command = commandPath
        .split('.')
        .reduce((obj, key) => obj?.[key], editor);
      if (typeof command === 'function') {
        command(...args);
      }
    }
  });
};

export const createMultiEditorProxy = (editorOrEditors) => {
  const editors = Array.isArray(editorOrEditors)
    ? editorOrEditors
    : [editorOrEditors];
  const firstEditor = editors[0];
  if (!firstEditor) return null;

  const editorsBySectionId = new Map(); // sectionId -> editor
  const tableSectionMap = new Map(); // tableId -> sectionId
  const elementSectionMap = new Map(); // elementId -> sectionId
  let lastMapBuildTime = 0;
  const MAP_REBUILD_INTERVAL = 10000; // 10 seconds

  const rebuildMaps = () => {
    const now = Date.now();
    if (now - lastMapBuildTime < MAP_REBUILD_INTERVAL) {
      return;
    }
    lastMapBuildTime = now;

    tableSectionMap.clear();
    elementSectionMap.clear();
    editorsBySectionId.clear();

    editors.forEach((editor, editorIndex) => {
      const sectionId = editor.storage?.sectionInfo?.sectionId;

      if (!sectionId) {
        console.warn(`[Drawer] Editor ${editorIndex} has no sectionId`);
        return;
      }

      editorsBySectionId.set(sectionId, editor);

      // Build maps by traversing doc
      try {
        editor.state.doc.descendants((node) => {
          if (node.attrs?.id) {
            const elementId = node.attrs.id;

            // Map ALL elements to sections
            elementSectionMap.set(elementId, sectionId);

            // Also track tables specifically for faster lookup
            if (node.type.name === 'table') {
              tableSectionMap.set(elementId, sectionId);
            }
          }
        });
      } catch (error) {
        console.error(
          `[Drawer] Error building maps for section ${sectionId}:`,
          error
        );
      }
    });
  };

  rebuildMaps();

  const findEditorWithElement = (elementId) => {
    let sectionId =
      elementSectionMap.get(elementId) || tableSectionMap.get(elementId);

    if (sectionId) {
      const editor = editorsBySectionId.get(sectionId);
      if (editor) {
        return editor;
      }
    }

    console.warn('[Drawer] Element not in map, rebuilding maps...', elementId);
    rebuildMaps();

    sectionId =
      elementSectionMap.get(elementId) || tableSectionMap.get(elementId);

    if (sectionId) {
      const editor = editorsBySectionId.get(sectionId);
      if (editor) {
        return editor;
      }
    }

    // Still not found - search DOM as fallback
    for (const editor of editors) {
      try {
        // Check if element exists in this editor's DOM
        const editorDom = editor.view.dom;
        const element = editorDom.querySelector(`[data-id="${elementId}"]`);

        if (element) {
          const foundSectionId = editor.storage?.sectionInfo?.sectionId;
          if (foundSectionId) {
            elementSectionMap.set(elementId, foundSectionId);
          }
          return editor;
        }
      } catch (error) {
        console.warn('[Drawer] Error searching editor for element:', error);
      }
    }

    console.warn('[Drawer] Element not found in any editor:', elementId);
    return firstEditor; // Fallback
  };

  const multiEditorCommands = {
    setFootingHighlights: (highlightedCells = []) => {
      if (highlightedCells.length === 0) {
        // Clear footing highlights in all sections
        editors.forEach((editor) => {
          const currentCells =
            editor?.storage?.highlightedTableCells?.footings || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setFootingHighlights([]);
          }
        });
        return true;
      }

      rebuildMaps();
      // Group cells by section
      const cellsBySection = new Map();

      highlightedCells.forEach((cell) => {
        const sectionId = tableSectionMap.get(cell.tableId);

        if (!sectionId) {
          console.warn('[Drawer] Table not found in map:', cell.tableId);
          return;
        }

        if (!cellsBySection.has(sectionId)) {
          cellsBySection.set(sectionId, []);
        }
        cellsBySection.get(sectionId).push(cell);
      });

      // Update only affected sections
      cellsBySection.forEach((cells, sectionId) => {
        const editor = editorsBySectionId.get(sectionId);
        if (editor?.commands) {
          editor.commands.setFootingHighlights(cells);
        }
      });

      // Clear sections that previously had highlights but now don't
      editorsBySectionId.forEach((editor, sectionId) => {
        if (!cellsBySection.has(sectionId)) {
          const currentCells =
            editor?.storage?.highlightedTableCells?.footings || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setFootingHighlights([]);
          }
        }
      });

      return true;
    },

    setFormattingHighlights: (highlightedCells = []) => {
      if (highlightedCells.length === 0) {
        // Clear formatting highlights in all sections
        editors.forEach((editor) => {
          const currentCells =
            editor?.storage?.highlightedTableCells?.formatting || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setFormattingHighlights([]);
          }
        });
        return true;
      }

      rebuildMaps();
      // Group cells by section
      const cellsBySection = new Map();

      highlightedCells.forEach((cell) => {
        const sectionId = tableSectionMap.get(cell.tableId);

        if (!sectionId) {
          console.warn('[Drawer] Table not found in map:', cell.tableId);
          return;
        }

        if (!cellsBySection.has(sectionId)) {
          cellsBySection.set(sectionId, []);
        }
        cellsBySection.get(sectionId).push(cell);
      });

      // Update only affected sections
      cellsBySection.forEach((cells, sectionId) => {
        const editor = editorsBySectionId.get(sectionId);
        if (editor?.commands) {
          editor.commands.setFormattingHighlights(cells);
        }
      });

      // Clear sections that previously had highlights but now don't
      editorsBySectionId.forEach((editor, sectionId) => {
        if (!cellsBySection.has(sectionId)) {
          const currentCells =
            editor?.storage?.highlightedTableCells?.formatting || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setFormattingHighlights([]);
          }
        }
      });

      return true;
    },

    setInconsistencyHighlights: (highlightedCells = []) => {
      if (highlightedCells.length === 0) {
        // Clear inconsistency highlights in all sections
        editors.forEach((editor) => {
          const currentCells =
            editor?.storage?.highlightedTableCells?.inconsistencies || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setInconsistencyHighlights([]);
          }
        });
        return true;
      }

      rebuildMaps();
      // Group cells by section
      const cellsBySection = new Map();

      highlightedCells.forEach((cell) => {
        const sectionId = tableSectionMap.get(cell.tableId);
        if (!sectionId) {
          console.warn('[Drawer] Table not found in map:', cell.tableId);
          return;
        }

        if (!cellsBySection.has(sectionId)) {
          cellsBySection.set(sectionId, []);
        }
        cellsBySection.get(sectionId).push(cell);
      });

      // Update only affected sections
      cellsBySection.forEach((cells, sectionId) => {
        const editor = editorsBySectionId.get(sectionId);
        if (editor?.commands) {
          editor.commands.setInconsistencyHighlights(cells);
        }
      });

      // Clear sections that previously had highlights but now don't
      editorsBySectionId.forEach((editor, sectionId) => {
        if (!cellsBySection.has(sectionId)) {
          const currentCells =
            editor?.storage?.highlightedTableCells?.inconsistencies || [];
          if (currentCells.length > 0 && editor?.commands) {
            editor.commands.setInconsistencyHighlights([]);
          }
        }
      });

      return true;
    },

    showHighlightedTableCells: (type) => {
      executeCommandOnEditors(
        editors,
        'commands.showHighlightedTableCells',
        type
      );
      return true;
    },

    hideHighlightedTableCells: () => {
      executeCommandOnEditors(editors, 'commands.hideHighlightedTableCells');
      return true;
    },

    clearSelection: (...args) => {
      executeCommandOnEditors(editors, 'commands.clearSelection', ...args);
      return true;
    },

    scrollToLocation: (scrollData, ...args) => {
      const targetEditor = findEditorWithElement(scrollData.id);

      if (targetEditor?.commands?.scrollToLocation) {
        // Defer scroll to next frame to avoid blocking current operation
        requestAnimationFrame(() => {
          targetEditor.commands.scrollToLocation(scrollData, ...args);
        });
      } else {
        console.warn(
          '[Drawer] Could not find editor for element:',
          scrollData.id
        );
      }

      return true;
    },

    setActiveHighlightedTableCell: (cellData, ...args) => {
      if (!cellData?.tableId) {
        console.warn(
          '[Drawer] setActiveHighlightedTableCell called without tableId'
        );
        return false;
      }

      rebuildMaps();
      const sectionId = tableSectionMap.get(cellData.tableId);
      const targetEditor = editorsBySectionId.get(sectionId);

      if (!targetEditor) {
        console.warn(
          '[Drawer] Table not found in any section:',
          cellData.tableId
        );
        return false;
      }

      if (targetEditor.commands?.setActiveHighlightedTableCell) {
        const setActiveHighlightedTableCell = () => {
          targetEditor.commands.setActiveHighlightedTableCell(
            cellData,
            ...args
          );
        };
        setTimeout(setActiveHighlightedTableCell, 100);
      }

      return true;
    },
  };

  return new Proxy(firstEditor, {
    get(target, prop) {
      if (prop === 'commands') {
        return new Proxy(target.commands, {
          get(commandsTarget, commandProp) {
            return (
              multiEditorCommands[commandProp] || commandsTarget[commandProp]
            );
          },
        });
      }
      // Special method to get JSON from all editors for document ID sorting
      if (prop === 'getAllEditorsJSON') {
        return () => {
          return editors.map((editor, index) => {
            try {
              return {
                editorIndex: index,
                sectionId: editor.storage?.sectionInfo?.sectionId,
                json: editor.getJSON(),
              };
            } catch (error) {
              console.warn('[Drawer] Error getting JSON from editor:', error);
              return {
                editorIndex: index,
                sectionId: null,
                json: {content: []},
              };
            }
          });
        };
      }
      return target[prop];
    },
  });
};
